import { createContext, useContext, useEffect, useState } from 'react';
import type { CodFormConfig } from '@xshop/shared';
import { getCodFormConfig } from '../api/codForm';
import { bootData } from '../api/client';

// The merchant's COD form is read once per visit, by the shell, and every placement — each buy button, the popup,
// the cart's Check out — reads that one copy from context. Each of them used to fetch its own: forty reads of the
// same form on one home page.

/** The form the shell loaded for this visit; null until it lands. */
export const CodFormContext = createContext<CodFormConfig | null>(null);

// Loads the merchant's COD form once for the visit; only the shell calls this, and provides what it loads.
export function useLoadedCodForm(): CodFormConfig | null {
  // The page's own answer when it carries one, so the first drawing — on the server too — already has the form.
  const [config, setConfig] = useState<CodFormConfig | null>(() => bootData<CodFormConfig>('/cod-form') ?? null);
  useEffect(() => {
    if (config) return;
    let cancelled = false;
    getCodFormConfig().then((loaded) => !cancelled && setConfig(loaded));
    return () => { cancelled = true; };
  }, []);
  return config;
}

// Reads the COD form the shell loaded for this visit, or null while it is still on its way.
export const useCodForm = (): CodFormConfig | null => useContext(CodFormContext);
